// WaitlistPage.tsx - Full-screen page shown to waitlisted users instead of the app.
// Collects contact details (email + optional note) while Rallify onboards in waves.
import React, { useEffect, useRef, useState } from "react";
import { DISCORD_INVITE_URL, DISCORD_ICON_PATH } from "./lib/links";

type MeLite = { username?: string } | null;

type Signup = {
  email: string;
  note: string | null;
  server: string | null;
  faction: string | null;
  guild_name: string | null;
  guild_rank: string | null;
  status: string;
  created_at: string;
} | null;

type GuildSuggestion = { name: string; server: string | null; signups: number };

const FACTIONS = ["Alliance", "Horde", "Either"] as const;
const RANKS = ["Guild Master", "Officer", "Member"] as const;

export default function WaitlistPage({ me }: { me: MeLite }) {
  const [loading, setLoading] = useState(true);
  const [signup, setSignup] = useState<Signup>(null);
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [server, setServer] = useState("");
  const [faction, setFaction] = useState("");
  const [guildName, setGuildName] = useState("");
  const [guildRank, setGuildRank] = useState("");
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Guild-name typeahead
  const [suggestions, setSuggestions] = useState<GuildSuggestion[]>([]);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  // Set right after picking a suggestion so we don't immediately re-query and
  // pop the list back open on the value we just committed.
  const skipNextLookup = useRef(false);

  useEffect(() => {
    const term = guildName.trim();
    if (skipNextLookup.current) {
      skipNextLookup.current = false;
      return;
    }
    if (term.length < 2) {
      setSuggestions([]);
      return;
    }
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`/waitlist/guild-suggestions?q=${encodeURIComponent(term)}`, {
          credentials: "include",
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!alive) return;
        setSuggestions(Array.isArray(data.suggestions) ? data.suggestions : []);
        setHighlighted(-1);
      } catch {
        /* typeahead is a convenience, stay quiet on failure */
      }
    }, 250);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [guildName]);

  const pickSuggestion = (s: GuildSuggestion) => {
    skipNextLookup.current = true;
    setGuildName(s.name);
    if (s.server && !server.trim()) setServer(s.server);
    setSuggestOpen(false);
    setHighlighted(-1);
  };

  const onGuildKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!suggestOpen || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === "Enter" && highlighted >= 0) {
      e.preventDefault(); // don't submit the form while choosing
      pickSuggestion(suggestions[highlighted]);
    } else if (e.key === "Escape") {
      setSuggestOpen(false);
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch("/waitlist/status", { credentials: "include" });
        if (!res.ok) throw new Error("status_failed");
        const data = await res.json();
        if (!alive) return;
        setSignup(data.signup || null);
        if (data.signup) {
          setEmail(data.signup.email || "");
          setNote(data.signup.note || "");
          setServer(data.signup.server || "");
          setFaction(data.signup.faction || "");
          skipNextLookup.current = true;
          setGuildName(data.signup.guild_name || "");
          setGuildRank(data.signup.guild_rank || "");
        }
      } catch {
        /* form stays available */
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const trimmed = email.trim();
    if (!/^\S+@\S+\.\S+$/.test(trimmed)) {
      setError("That doesn't look like a valid email.");
      return;
    }
    if (!server.trim()) {
      setError("Please tell us your current server.");
      return;
    }
    if (!FACTIONS.includes(faction as any)) {
      setError("Please pick your faction.");
      return;
    }
    const trimmedGuild = guildName.trim();
    if (trimmedGuild && !RANKS.includes(guildRank as any)) {
      setError("Please pick your rank in the guild.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("/waitlist/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: trimmed,
          note: note.trim(),
          server: server.trim(),
          faction,
          guildName: trimmedGuild,
          guildRank: trimmedGuild ? guildRank : "",
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        const msg =
          j.error === "invalid_email" ? "That doesn't look like a valid email." :
          j.error === "server_required" ? "Please tell us your current server." :
          j.error === "invalid_faction" ? "Please pick your faction." :
          j.error === "invalid_rank" ? "Please pick your rank in the guild." :
          "Something went wrong. Try again.";
        throw new Error(msg);
      }
      setSignup({
        email: trimmed,
        note: note.trim() || null,
        server: server.trim(),
        faction,
        guild_name: trimmedGuild || null,
        guild_rank: trimmedGuild ? guildRank : null,
        status: "pending",
        created_at: signup?.created_at || new Date().toISOString(),
      });
      setEditing(false);
    } catch (err: any) {
      setError(err?.message || "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const logout = async () => {
    try {
      await fetch("/logout", { method: "POST", credentials: "include" });
    } finally {
      window.location.reload();
    }
  };

  const showForm = !signup || editing;

  return (
    <div className="min-h-screen w-full bg-[#0D1014] text-white flex flex-col items-center justify-center px-6 py-12">
      <div className="w-full max-w-lg">
        {/* Wordmark */}
        <div className="flex items-center gap-3 justify-center mb-10">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-amber-500/25 to-amber-600/10 ring-1 ring-amber-400/40 flex items-center justify-center font-black text-amber-200 shadow-[0_0_28px_rgba(245,158,11,0.25)]">
            <span className="text-xl">R</span>
          </div>
          <div className="flex flex-col">
            <span className="text-base font-bold tracking-wide leading-none">RALLIFY</span>
            <span className="text-[10px] text-white/30 font-medium tracking-wider uppercase">Less Searching, More Playing</span>
          </div>
        </div>

        <div className="rounded-2xl border border-[#1C2028] bg-[#10141B] p-8 shadow-2xl shadow-black/40">
          {loading ? (
            <div className="text-white/40 text-sm text-center py-10 animate-pulse">Loading...</div>
          ) : showForm ? (
            <>
              <h1 className="text-3xl font-black tracking-tight mb-2">
                You're <span className="text-amber-400">almost</span> in.
              </h1>
              <p className="text-sm text-white/50 leading-relaxed mb-6">
                Rallify is onboarding players in waves to keep raid tools fast for everyone.
                Leave your email and we'll open the gates for you as soon as a spot frees up.
              </p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-amber-400/90 mb-1.5">Email</label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full rounded-lg bg-[#0D1014] border border-[#1C2028] focus:border-amber-500/60 focus:outline-none px-4 py-3 text-sm text-white placeholder-white/25"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-wider text-amber-400/90 mb-1.5">Current server</label>
                    <input
                      type="text"
                      required
                      value={server}
                      onChange={(e) => setServer(e.target.value)}
                      maxLength={100}
                      placeholder="e.g. Spineshatter"
                      className="w-full rounded-lg bg-[#0D1014] border border-[#1C2028] focus:border-amber-500/60 focus:outline-none px-4 py-3 text-sm text-white placeholder-white/25"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-wider text-amber-400/90 mb-1.5">Faction</label>
                    <div className="flex gap-2">
                      {FACTIONS.map((f) => {
                        const active = faction === f;
                        const accent =
                          f === "Alliance" ? "border-sky-400/70 bg-sky-500/10 text-sky-300" :
                          f === "Horde" ? "border-red-400/70 bg-red-500/10 text-red-300" :
                          "border-amber-400/70 bg-amber-500/10 text-amber-300";
                        return (
                          <button
                            key={f}
                            type="button"
                            onClick={() => setFaction(f)}
                            className={`flex-1 rounded-lg border px-2 py-3 text-xs font-bold transition-colors ${
                              active ? accent : "border-[#1C2028] bg-[#0D1014] text-white/40 hover:text-white/70 hover:border-white/20"
                            }`}
                          >
                            {f}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-amber-400/90 mb-1.5">
                    Guild <span className="text-white/30 normal-case font-medium">(optional)</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={guildName}
                      onChange={(e) => {
                        setGuildName(e.target.value);
                        setSuggestOpen(true);
                      }}
                      onFocus={() => setSuggestOpen(true)}
                      onBlur={() => setTimeout(() => setSuggestOpen(false), 120)}
                      onKeyDown={onGuildKeyDown}
                      maxLength={100}
                      autoComplete="off"
                      placeholder="Start typing your guild name..."
                      className="w-full rounded-lg bg-[#0D1014] border border-[#1C2028] focus:border-amber-500/60 focus:outline-none px-4 py-3 text-sm text-white placeholder-white/25"
                    />
                    {suggestOpen && suggestions.length > 0 && (
                      <ul className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-[#1C2028] bg-[#141922] shadow-xl shadow-black/60">
                        {suggestions.map((s, i) => (
                          <li key={s.name}>
                            <button
                              type="button"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => pickSuggestion(s)}
                              onMouseEnter={() => setHighlighted(i)}
                              className={`flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-sm transition-colors ${
                                highlighted === i ? "bg-amber-500/15 text-white" : "text-white/70 hover:bg-white/5"
                              }`}
                            >
                              <span className="truncate font-semibold">{s.name}</span>
                              <span className="shrink-0 text-[11px] text-white/35">
                                {[s.server, `${s.signups} signed up`].filter(Boolean).join(" · ")}
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <p className="mt-1.5 text-[11px] text-white/30">
                    Pick your guild from the list if it shows up, so we can group your raid team together.
                  </p>
                </div>
                {guildName.trim() !== "" && (
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-wider text-amber-400/90 mb-1.5">Your rank</label>
                    <div className="flex gap-2">
                      {RANKS.map((r) => {
                        const active = guildRank === r;
                        return (
                          <button
                            key={r}
                            type="button"
                            onClick={() => setGuildRank(r)}
                            className={`flex-1 rounded-lg border px-2 py-3 text-xs font-bold transition-colors ${
                              active
                                ? "border-amber-400/70 bg-amber-500/10 text-amber-300"
                                : "border-[#1C2028] bg-[#0D1014] text-white/40 hover:text-white/70 hover:border-white/20"
                            }`}
                          >
                            {r}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-amber-400/90 mb-1.5">
                    Your raid team <span className="text-white/30 normal-case font-medium">(optional)</span>
                  </label>
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={3}
                    maxLength={2000}
                    placeholder="Guild name, roster size, game version. Helps us prioritize."
                    className="w-full rounded-lg bg-[#0D1014] border border-[#1C2028] focus:border-amber-500/60 focus:outline-none px-4 py-3 text-sm text-white placeholder-white/25 resize-none"
                  />
                </div>
                {error && <div className="text-sm text-red-400">{error}</div>}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full rounded-lg bg-amber-500 hover:bg-amber-400 disabled:opacity-50 px-4 py-3 text-sm font-bold text-black transition-colors"
                >
                  {submitting ? "Saving..." : signup ? "Update details" : "Join the waitlist"}
                </button>
                {editing && (
                  <button type="button" onClick={() => setEditing(false)} className="w-full text-xs text-white/40 hover:text-white/70">
                    Cancel
                  </button>
                )}
              </form>
            </>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 rounded-full bg-emerald-500/15 ring-1 ring-emerald-400/40 flex items-center justify-center">
                  <svg className="h-5 w-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h1 className="text-2xl font-black tracking-tight">You're on the list.</h1>
              </div>
              <p className="text-sm text-white/50 leading-relaxed mb-4">
                We'll email <span className="text-amber-300 font-semibold">{signup.email}</span> the moment your spot opens.
                Signed up {new Date(signup.created_at).toLocaleDateString()}.
              </p>
              {(signup.server || signup.faction || signup.guild_name) && (
                <p className="text-xs text-white/40 mb-4">
                  {[
                    signup.server,
                    signup.faction,
                    signup.guild_name && signup.guild_rank
                      ? `${signup.guild_name} (${signup.guild_rank})`
                      : signup.guild_name,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              )}
              <button onClick={() => setEditing(true)} className="text-xs font-semibold text-amber-400 hover:text-amber-300">
                Edit details
              </button>
            </>
          )}
        </div>

        {/* Rallify community Discord. Shown only once they are actually on the
            list, so it reads as "here is where to wait with us" rather than as
            another step in signing up. This is the Rallify server, not a guild's. */}
        {!editing && signup && (
          <a
            href={DISCORD_INVITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 group flex items-center gap-4 rounded-xl border border-indigo-500/25 bg-indigo-500/5 px-5 py-4 transition-colors hover:border-indigo-400/40 hover:bg-indigo-500/10"
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#5865F2]">
              <svg className="h-5 w-5 text-white" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d={DISCORD_ICON_PATH} />
              </svg>
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-bold text-white">Join the Rallify community</span>
              <span className="block text-xs text-white/50 leading-relaxed">
                Our official Discord, not a guild server. Say hello while you wait.
              </span>
            </span>
            <svg className="h-4 w-4 shrink-0 text-white/30 transition-transform group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </a>
        )}

        {/* Instant-access hint */}
        <div className="mt-4 rounded-xl border border-amber-500/20 bg-amber-500/5 px-5 py-4">
          <p className="text-xs text-white/55 leading-relaxed">
            <span className="font-bold text-amber-400">Skip the line:</span> already raiding with a guild on Rallify?
            Ask your guild lead for an invite link, or join a raid through a PUG link. Those get you in instantly.
          </p>
        </div>

        <div className="mt-8 flex items-center justify-between text-xs text-white/30">
          <span>Signed in as <span className="text-white/60 font-semibold">{me?.username || "Discord user"}</span></span>
          <button onClick={logout} className="hover:text-white/70 font-semibold">Log out</button>
        </div>
      </div>
    </div>
  );
}
